export interface CaseStudy {
  slug: string;
  title: string;
  summary: string;
  category: "ransomware" | "dsgvo" | "phishing" | "datenverlust" | "ddos" | "betriebsunterbrechung";
  practiceType: string;
  date: string;
  totalDamage: string;
  downtime: string;
  affectedPatients: number;
  incident: string;
  costs: Array<{
    category: string;
    description: string;
    amount: string;
  }>;
  insuranceCoverage: {
    provider: string;
    coverage: string;
    payout: string;
    coveredItems: string[];
  };
  lessonsLearned: string[];
  recommendedTariffs: Array<{
    provider: string;
    slug: string;
    reason: string;
    pricing: string;
  }>;
  relatedCases: string[];
}

export const caseStudies: CaseStudy[] = [
  {
    slug: "ransomware-hausarztpraxis-muenchen",
    title: "Ransomware-Angriff legt Hausarztpraxis 5 Tage lahm",
    summary: "Eine Hausarztpraxis in München wurde Opfer eines Ransomware-Angriffs. Alle Systeme waren verschlüsselt, 1.200 Patientenakten unzugänglich. Gesamtschaden: 89.000 €.",
    category: "ransomware",
    practiceType: "Hausarztpraxis",
    date: "2024-03-15",
    totalDamage: "89.000 €",
    downtime: "5 Tage",
    affectedPatients: 1200,
    incident: `Tag 1 (Montag, 07:15 Uhr): MFA öffnet vermeintliche E-Mail vom Labor mit Befunden. PDF-Anhang enthält Ransomware.

Tag 1 (07:30 Uhr): Ransomware beginnt Verschlüsselung aller Netzlaufwerke. Praxissoftware wird unbrauchbar.

Tag 1 (08:00 Uhr): Praxis bemerkt Angriff. Alle PCs zeigen Lösegeldforderung (15.000 € in Bitcoin, 48h Frist).

Tag 1 (09:00 Uhr): IT-Dienstleister informiert. Polizei und Cyberversicherung werden kontaktiert. DSGVO-Meldung vorbereitet.

Tag 1-2: Forensische Untersuchung läuft. Praxis arbeitet mit Papier-Akten. 60% Termine werden abgesagt.

Tag 3: Wiederherstellung aus Backups beginnt. Letztes Backup ist 3 Tage alt.

Tag 5: Systeme wieder funktionsfähig. Normale Praxis-Tätigkeit wird aufgenommen.`,
    costs: [
      {
        category: "IT-Forensik & Wiederherstellung",
        description: "Spezialisierte IT-Forensiker zur Untersuchung und Systemwiederherstellung",
        amount: "18.500 €"
      },
      {
        category: "Betriebsunterbrechung",
        description: "Umsatzverlust durch 5 Tage Praxis-Schließung (ca. 60% Kapazität)",
        amount: "42.000 €"
      },
      {
        category: "Hardware-Ersatz",
        description: "3 kompromittierte PCs mussten ersetzt werden",
        amount: "4.200 €"
      },
      {
        category: "Datenschutz & Recht",
        description: "DSGVO-Meldung, Rechtsberatung, Patientenbenachrichtigung",
        amount: "12.800 €"
      },
      {
        category: "Notfall-IT-Support",
        description: "24/7-Support während Wiederherstellung (5 Tage)",
        amount: "8.500 €"
      },
      {
        category: "Präventionsmaßnahmen",
        description: "Neue Endpoint-Protection, Schulungen, Backup-Optimierung",
        amount: "3.000 €"
      }
    ],
    insuranceCoverage: {
      provider: "Allianz Cyber-Schutz Premium",
      coverage: "3 Mio. € Deckungssumme",
      payout: "85.200 €",
      coveredItems: [
        "Vollständige IT-Forensik und Datenwiederherstellung",
        "Betriebsunterbrechungskosten (80% Deckung)",
        "Hardware-Ersatzbeschaffung",
        "Rechtsberatung und DSGVO-Compliance",
        "24/7-Notfall-Hotline und Krisenmanagement",
        "Cyber-Security-Schulungen nach Vorfall"
      ]
    },
    lessonsLearned: [
      "Offline-Backups hätten Wiederherstellung auf 24h verkürzt - 3-2-1-Backup-Regel umgesetzt",
      "E-Mail-Sicherheit wurde massiv verstärkt: ATP-Filter + Phishing-Simulation alle 3 Monate",
      "Endpoint-Detection-Response (EDR) statt klassischem Virenschutz implementiert",
      "Netzwerksegmentierung: Praxis-PCs jetzt isoliert vom Verwaltungsnetz",
      "Incident-Response-Plan erstellt mit klaren Verantwortlichkeiten",
      "Alle Mitarbeiter absolvierten Cyber-Security-Training"
    ],
    recommendedTariffs: [
      {
        provider: "Allianz Cyber-Schutz",
        slug: "allianz",
        reason: "Testsieger für Hausarztpraxen. Exzellente Soforthilfe, niedrige Selbstbeteiligung.",
        pricing: "79 €/Monat"
      },
      {
        provider: "Hiscox CyberClear",
        slug: "hiscox",
        reason: "Spezialist mit höchster Deckung für Betriebsunterbrechung. Inkl. Präventions-Tools.",
        pricing: "95 €/Monat"
      },
      {
        provider: "VHV Cyber-Protection",
        slug: "vhv",
        reason: "Starkes Preis-Leistungs-Verhältnis. Sehr gute Bewertungen im Schadenfall.",
        pricing: "68 €/Monat"
      }
    ],
    relatedCases: ["dsgvo-verstoß-zahnarztpraxis-koeln", "phishing-mvz-hamburg"]
  },
  {
    slug: "dsgvo-verstoß-zahnarztpraxis-koeln",
    title: "DSGVO-Bußgeld nach Datenpanne bei Zahnarztpraxis",
    summary: "Zahnarztpraxis in Köln erlitt Datenpanne durch Phishing. 850 Patientendaten kompromittiert. Verspätete Meldung führte zu 45.000 € Bußgeld.",
    category: "dsgvo",
    practiceType: "Zahnarztpraxis",
    date: "2024-01-20",
    totalDamage: "67.800 €",
    downtime: "2 Tage",
    affectedPatients: 850,
    incident: `Tag 1 (Mittwoch, 14:30 Uhr): Zahnarzthelferin erhält E-Mail vermeintlich von KZV mit Aufforderung, Abrechnungsdaten zu aktualisieren. Sie klickt Link und gibt Zugangsdaten ein.

Tag 1 (15:00 Uhr): Angreifer loggt sich mit gestohlenen Credentials in Praxissoftware ein und exfiltriert 850 Patientendatensätze.

Tag 3 (Freitag): Praxis bemerkt ungewöhnliche Login-Aktivitäten. IT-Dienstleister wird informiert.

Tag 8: Nach forensischer Analyse wird Umfang der Datenpanne klar. Verspätete DSGVO-Meldung (>72h-Frist überschritten).

Tag 12: Datenschutzbehörde leitet Bußgeldverfahren ein wegen verspäteter Meldung und unzureichender technischer Maßnahmen (keine 2FA).

Tag 45: Bußgeldbescheid über 45.000 € trifft ein.`,
    costs: [
      {
        category: "DSGVO-Bußgeld",
        description: "Bußgeld wegen verspäteter Meldung und unzureichender TOMs",
        amount: "45.000 €"
      },
      {
        category: "Forensik & Datenschutz-Audit",
        description: "Untersuchung des Vorfalls und Datenschutz-Folgenabschätzung",
        amount: "8.900 €"
      },
      {
        category: "Patientenbenachrichtigung",
        description: "Anwaltliche Prüfung + Versand von 850 Benachrichtigungsschreiben",
        amount: "5.200 €"
      },
      {
        category: "Rechtsberatung & Behördenkommunikation",
        description: "Anwaltliche Vertretung im Bußgeldverfahren",
        amount: "6.500 €"
      },
      {
        category: "Reputationsschaden-Management",
        description: "PR-Beratung und Krisenkommunikation",
        amount: "2.200 €"
      }
    ],
    insuranceCoverage: {
      provider: "ERGO Cyber-Versicherung Plus",
      coverage: "2 Mio. € Deckungssumme",
      payout: "62.300 €",
      coveredItems: [
        "DSGVO-Bußgeld vollständig übernommen (auch bei Fahrlässigkeit)",
        "Forensische Untersuchung und Datenschutz-Audit",
        "Kosten für Patientenbenachrichtigung",
        "Anwaltskosten im Bußgeldverfahren",
        "Krisenmanagement und Reputationsschutz",
        "Datenschutz-Schulungen nach Vorfall"
      ]
    },
    lessonsLearned: [
      "Zwei-Faktor-Authentifizierung (2FA) wurde für alle Systemzugänge verpflichtend eingeführt",
      "Incident-Response-Plan mit klaren 72h-DSGVO-Meldefristen erstellt",
      "Datenschutzbeauftragten extern bestellt für professionelle Compliance",
      "Phishing-Simulationen vierteljährlich für alle Mitarbeiter",
      "Security-Monitoring mit automatischen Alerts bei ungewöhnlichen Zugriffen",
      "Verarbeitungsverzeichnis und TOMs vollständig dokumentiert"
    ],
    recommendedTariffs: [
      {
        provider: "ERGO Cyber Plus",
        slug: "ergo",
        reason: "Deckt DSGVO-Bußgelder auch bei Fahrlässigkeit. Inkl. Datenschutz-Hotline 24/7.",
        pricing: "72 €/Monat"
      },
      {
        provider: "Gothaer Gewerbe-Protect Cyber",
        slug: "gothaer",
        reason: "Spezialisiert auf DSGVO-Compliance. Präventive Datenschutz-Audits inklusive.",
        pricing: "85 €/Monat"
      },
      {
        provider: "Markel Cyber-Schutz",
        slug: "markel",
        reason: "Hohe Deckung für Regulierungskosten. Sehr erfahren bei Bußgeldverfahren.",
        pricing: "92 €/Monat"
      }
    ],
    relatedCases: ["ransomware-hausarztpraxis-muenchen", "phishing-mvz-hamburg"]
  },
  {
    slug: "phishing-mvz-hamburg",
    title: "CEO-Fraud: MVZ verliert 125.000 € durch Phishing",
    summary: "Ein MVZ in Hamburg wurde Opfer eines ausgefeilten CEO-Fraud-Angriffs. Geschäftsführung wurde vorgetäuscht, 125.000 € überwiesen.",
    category: "phishing",
    practiceType: "MVZ (Medizinisches Versorgungszentrum)",
    date: "2024-02-10",
    totalDamage: "138.500 €",
    downtime: "0 Tage",
    affectedPatients: 0,
    incident: `Tag 1 (Donnerstag, 11:45 Uhr): Buchhaltung erhält E-Mail vermeintlich vom Geschäftsführer (gefälschte E-Mail-Adresse, sehr professionell). Dringende Überweisung für "vertrauliche Akquisition" gefordert.

Tag 1 (12:15 Uhr): Buchhalterin ruft unter angegebener Telefonnummer an - Angreifer gibt sich als GF aus, bestätigt Dringlichkeit.

Tag 1 (14:00 Uhr): Überweisung von 125.000 € wird ausgeführt auf ausländisches Konto.

Tag 2 (Freitag, 10:00 Uhr): Echter Geschäftsführer bemerkt unbekannte Überweisung. Sofortige Kontaktaufnahme mit Bank - zu spät, Geld bereits weitergeleitet.

Tag 2 (11:00 Uhr): Polizei und Cyberversicherung werden informiert. Strafanzeige gegen Unbekannt.

Tag 7: Forensische Untersuchung zeigt: Angreifer hatten vorher E-Mail-Verkehr abgefangen und Muster studiert.

Ergebnis: 95.000 € unwiederbringlich verloren, Rest durch Versicherung gedeckt.`,
    costs: [
      {
        category: "Direkter Vermögensschaden",
        description: "Durch Betrug verlorene 125.000 € (davon 95.000 € nicht rückerstattet)",
        amount: "95.000 €"
      },
      {
        category: "Forensik & IT-Sicherheits-Audit",
        description: "Untersuchung des E-Mail-Verkehrs und Security-Assessment",
        amount: "12.500 €"
      },
      {
        category: "Rechtliche Aufarbeitung",
        description: "Strafanzeige, Zivilrechtliche Verfolgung, Bankkommunikation",
        amount: "18.000 €"
      },
      {
        category: "IT-Sicherheits-Upgrades",
        description: "E-Mail-Security-Gateway, Anti-Phishing-Tools, DMARC/SPF/DKIM",
        amount: "8.000 €"
      },
      {
        category: "Schulungen & Awareness",
        description: "Intensive Phishing-Awareness-Trainings für alle Mitarbeiter",
        amount: "5.000 €"
      }
    ],
    insuranceCoverage: {
      provider: "Zurich Cyber-Versicherung Professional",
      coverage: "5 Mio. € Deckungssumme",
      payout: "43.500 €",
      coveredItems: [
        "Forensische Untersuchung vollständig",
        "Rechtskosten und Strafverfolgung",
        "IT-Sicherheits-Upgrades (Selbstbeteiligung 20%)",
        "Vertrauensschaden-Deckung (30.000 € Teilerstattung)",
        "Cyber-Security-Schulungen nach Vorfall",
        "Präventions-Beratung durch Cyber-Experten"
      ]
    },
    lessonsLearned: [
      "Vier-Augen-Prinzip bei Überweisungen > 10.000 € strikt umgesetzt",
      "Rückruf-Verfahren: Bestätigung via bekannter Nummer, nicht aus E-Mail",
      "E-Mail-Authentifizierung DMARC/SPF/DKIM implementiert",
      "Erweiterte E-Mail-Security-Gateway mit KI-basierter Anomalieerkennung",
      "Regelmäßige Phishing-Simulationen (monatlich) mit Auswertung",
      "Notfall-Kontakte bei Bank für schnelle Transaktions-Stopps hinterlegt"
    ],
    recommendedTariffs: [
      {
        provider: "Zurich Cyber Professional",
        slug: "zurich",
        reason: "Beste Deckung für Vermögensschäden durch Social Engineering. Hohe Limits.",
        pricing: "145 €/Monat"
      },
      {
        provider: "AXA Cyber-Protection Premium",
        slug: "axa",
        reason: "Starke Vertrauensschaden-Deckung. Präventions-Tools inklusive.",
        pricing: "138 €/Monat"
      },
      {
        provider: "Cogitanda CyberProtect",
        slug: "cogitanda",
        reason: "Spezialist für komplexe MVZ-Strukturen. Sehr gute Schadenregulierung.",
        pricing: "152 €/Monat"
      }
    ],
    relatedCases: ["ransomware-hausarztpraxis-muenchen", "dsgvo-verstoß-zahnarztpraxis-koeln"]
  },
  {
    slug: "datenverlust-facharztpraxis-berlin",
    title: "Datenverlust durch defekte Server-Hardware",
    summary: "Facharztpraxis verlor 3 Jahre Patientendaten durch Hardware-Ausfall. Unzureichende Backups führten zu massivem Datenverlust.",
    category: "datenverlust",
    practiceType: "Facharztpraxis (Orthopädie)",
    date: "2023-11-05",
    totalDamage: "52.300 €",
    downtime: "8 Tage",
    affectedPatients: 2400,
    incident: `Tag 1 (Montag, 08:00 Uhr): Server startet nach Wochenende nicht mehr. IT-Dienstleister wird gerufen.

Tag 1 (14:00 Uhr): Hardware-Defekt diagnostiziert - RAID-Controller defekt, mehrere Festplatten beschädigt.

Tag 2: Datenrettungsversuch beginnt. Letztes funktionierendes Backup ist 18 Monate alt.

Tag 2-5: Speziallabor versucht Datenrettung von beschädigten Festplatten.

Tag 6: Teilweise Datenrettung erfolgreich - aber 40% der Daten der letzten 18 Monate unwiederbringlich verloren.

Tag 6-8: Manuelle Rekonstruktion kritischer Patientendaten aus Papier-Unterlagen und Anfragen bei Krankenkassen.

Tag 8: Notbetrieb mit neuem Server und teilweise wiederhergestellten Daten. 960 Patienten müssen über Datenverlust informiert werden.`,
    costs: [
      {
        category: "Professionelle Datenrettung",
        description: "Speziallabor für Hardware-Datenrettung (4 Tage intensive Arbeit)",
        amount: "18.900 €"
      },
      {
        category: "Hardware-Ersatz & Neueinrichtung",
        description: "Neuer Server, RAID-System, Installation und Migration",
        amount: "12.400 €"
      },
      {
        category: "Betriebsunterbrechung",
        description: "8 Tage reduzierter Betrieb, viele Termine abgesagt",
        amount: "14.500 €"
      },
      {
        category: "Manuelle Datenrekonstruktion",
        description: "Mitarbeiter-Überstunden, externe Unterstützung",
        amount: "4.200 €"
      },
      {
        category: "DSGVO-Dokumentation",
        description: "Meldung, Patientenbenachrichtigung, Datenschutz-Folgenabschätzung",
        amount: "2.300 €"
      }
    ],
    insuranceCoverage: {
      provider: "Württembergische Cyber-Versicherung",
      coverage: "1,5 Mio. € Deckungssumme",
      payout: "48.100 €",
      coveredItems: [
        "Professionelle Datenrettung vollständig",
        "Hardware-Ersatzbeschaffung",
        "Betriebsunterbrechungskosten (90% Deckung)",
        "Kosten für Datenrekonstruktion",
        "DSGVO-Melde- und Dokumentationspflichten",
        "Neue Backup-Infrastruktur (50% Förderung)"
      ]
    },
    lessonsLearned: [
      "3-2-1-Backup-Strategie konsequent implementiert: 3 Kopien, 2 Medien, 1 offline",
      "Tägliche automatische Backups statt wöchentlich",
      "Cloud-Backup als zusätzliche Absicherung außerhalb der Praxis",
      "Monatliche Restore-Tests zur Backup-Validierung",
      "Hardware-Monitoring mit proaktiven Warnungen bei RAID-Problemen",
      "Dokumentation aller kritischen Systeme und Wiederherstellungsprozesse"
    ],
    recommendedTariffs: [
      {
        provider: "Württembergische Cyber",
        slug: "wuerttembergische",
        reason: "Exzellente Datenrettungs-Deckung. Schnelle Schadenabwicklung.",
        pricing: "58 €/Monat"
      },
      {
        provider: "Nürnberger Cyber-Schutz",
        slug: "nuernberger",
        reason: "Starke Betriebsunterbrechungs-Deckung. Backup-Consulting inklusive.",
        pricing: "64 €/Monat"
      },
      {
        provider: "R+V Cyber-Versicherung",
        slug: "rv",
        reason: "Gutes Preis-Leistungs-Verhältnis für Facharztpraxen.",
        pricing: "55 €/Monat"
      }
    ],
    relatedCases: ["ransomware-hausarztpraxis-muenchen"]
  },
  {
    slug: "ddos-angriff-klinik-frankfurt",
    title: "DDoS-Angriff legt Klinik-Website 3 Tage lahm",
    summary: "Privatklinik in Frankfurt wurde Ziel eines massiven DDoS-Angriffs. Online-Terminbuchung und Patientenportal 72h offline.",
    category: "ddos",
    practiceType: "Privatklinik",
    date: "2024-04-12",
    totalDamage: "34.700 €",
    downtime: "3 Tage",
    affectedPatients: 0,
    incident: `Tag 1 (Freitag, 18:00 Uhr): Website wird langsam, dann komplett unerreichbar. DDoS-Angriff mit 450 Gbps Traffic.

Tag 1 (19:00 Uhr): Hosting-Provider informiert, rudimentäre DDoS-Mitigation aktiviert - reicht nicht aus.

Tag 1-3 (Wochenende): Website bleibt offline. Online-Terminbuchung nicht verfügbar. Patienten können Portal nicht nutzen.

Tag 2: Externe DDoS-Protection-Service wird beauftragt. Migration der DNS-Einträge.

Tag 3: DDoS-Protection aktiv, Angriff wird gefiltert. Website wieder erreichbar.

Tag 4 (Montag): Analyse zeigt: Erpressungsversuch - Klinik hatte Bitcoin-Forderung übersehen. Keine Zahlung erfolgt.`,
    costs: [
      {
        category: "DDoS-Mitigation-Service",
        description: "Professionelle DDoS-Protection (3 Monate Mindestlaufzeit)",
        amount: "12.400 €"
      },
      {
        category: "Hosting-Upgrades",
        description: "Leistungsstärkeres Hosting mit integrierter DDoS-Protection",
        amount: "6.800 €"
      },
      {
        category: "Betriebsunterbrechung",
        description: "Entgangene Online-Terminbuchungen, Mehraufwand telefonische Terminierung",
        amount: "8.500 €"
      },
      {
        category: "IT-Forensik & Sicherheitsaudit",
        description: "Analyse des Angriffs und Security-Assessment",
        amount: "4.200 €"
      },
      {
        category: "Kommunikation & Reputationsschutz",
        description: "Patienteninformation, PR-Beratung",
        amount: "2.800 €"
      }
    ],
    insuranceCoverage: {
      provider: "HDI Cyber-Versicherung Premium",
      coverage: "3 Mio. € Deckungssumme",
      payout: "31.900 €",
      coveredItems: [
        "DDoS-Mitigation-Kosten vollständig",
        "Hosting-Upgrades und Infrastruktur-Verbesserungen",
        "Betriebsunterbrechungskosten (80% Deckung)",
        "IT-Forensik und Security-Audit",
        "Reputationsmanagement und Krisenkommunikation",
        "Präventive DDoS-Tests und Consulting"
      ]
    },
    lessonsLearned: [
      "Professionelle DDoS-Protection als Standard für alle öffentlich erreichbaren Systeme",
      "Content Delivery Network (CDN) mit integriertem DDoS-Schutz implementiert",
      "Incident-Response-Plan für DDoS-Szenarien erstellt",
      "Monitoring mit automatischen Alerts bei Traffic-Anomalien",
      "Backup-Kommunikationskanäle für Patienteninformation (Social Media, E-Mail)",
      "Regelmäßige Penetrationstests und DDoS-Simulationen"
    ],
    recommendedTariffs: [
      {
        provider: "HDI Cyber Premium",
        slug: "hdi",
        reason: "Top-Deckung für DDoS und Betriebsunterbrechung. Klinik-Spezialist.",
        pricing: "165 €/Monat"
      },
      {
        provider: "Signal Iduna Cyber-Protect",
        slug: "signal-iduna",
        reason: "Sehr gute Cyber-Erpressungs-Deckung. Schnelle Schadenabwicklung.",
        pricing: "148 €/Monat"
      },
      {
        provider: "Alte Leipziger Cyber",
        slug: "alte-leipziger",
        reason: "Starkes Preis-Leistungs-Verhältnis für Kliniken.",
        pricing: "142 €/Monat"
      }
    ],
    relatedCases: []
  }
];

// Helper functions
export const getCaseStudyBySlug = (slug: string): CaseStudy | undefined => {
  return caseStudies.find(cs => cs.slug === slug);
};

export const getCaseStudiesByCategory = (category: CaseStudy["category"]): CaseStudy[] => {
  return caseStudies.filter(cs => cs.category === category);
};

export const getCaseStudiesByPracticeType = (practiceType: string): CaseStudy[] => {
  return caseStudies.filter(cs => cs.practiceType.toLowerCase().includes(practiceType.toLowerCase()));
};
